import Big from 'big.js'
import { isNil } from 'lodash-es'
import { Decimal } from 'utils'
import { NumberFormatOptions } from 'locale'

export interface NumericRange {
  min?: number | null | undefined
  max?: number | null | undefined
}

export function canStepUp(currentValue: number | null | undefined, range: NumericRange = {}) {
  const maxValue = getMaxValue(range)
  return isNil(currentValue) ? maxValue >= 0 : currentValue < maxValue
}

export function canStepDown(currentValue: number | null | undefined, range: NumericRange = {}) {
  const minValue = getMinValue(range)
  return isNil(currentValue) ? minValue <= 0 : currentValue > minValue
}

export function resolveStep(step: number | null | undefined, value: number | null | undefined, options: NumberFormatOptions): number {
  const { decimalShift, minDp, maxDp } = options
  const actualDecimalShift = decimalShift || 0

  if (step) {
    if (isNil(maxDp)) {
      return Decimal.shift(step, actualDecimalShift)
    }
    const bigStep = new Big(Decimal.shift(step, actualDecimalShift))
    return Decimal.toNumber(bigStep.round(maxDp)) || 1
  } else if (!isNil(minDp)) {
    const actualMinDp = minDp || 0
    return Decimal.shift(1, -actualMinDp - actualDecimalShift)
  } else if (!isNil(value)) {
    const actualDp = Decimal.getDecimalPlaces(value)
    return Decimal.shift(1, -actualDp)
  } else {
    return Decimal.shift(1, -actualDecimalShift)
  }
}

export function stepUp(currentValue: number | null | undefined, step: number, range: NumericRange = {}) {
  currentValue = currentValue || 0
  const newValue = Decimal.add(currentValue, step)
  const roundedValue = Decimal.round(newValue, step, Decimal.RoundingMode.Down)
  return ensureWithinRange(roundedValue, range)
}

export function stepDown(currentValue: number | null | undefined, step: number, range: NumericRange = {}) {
  currentValue = currentValue || 0
  const newValue = Decimal.subtract(currentValue, step)
  const roundedValue = Decimal.round(newValue, step, Decimal.RoundingMode.Up)
  return ensureWithinRange(roundedValue, range)
}

function getMinValue(range: NumericRange) {
  const min = range && range.min
  return isNil(min) ? -Number.MAX_SAFE_INTEGER : min
}

function getMaxValue(range: NumericRange) {
  const max = range && range.max
  return isNil(max) ? Number.MAX_SAFE_INTEGER : max
}

function ensureWithinRange(currentValue: number, range: NumericRange = {}) {
  const minValue = getMinValue(range)
  const maxValue = getMaxValue(range)

  if (currentValue < minValue) {
    return minValue
  } else if (currentValue > maxValue) {
    return maxValue
  } else {
    return currentValue
  }
}
